import React, { useEffect, useMemo, useState } from "react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast, Toaster } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { createBooking, getTakenSlots } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Video,
  Phone,
  Mail,
  Lock,
  Globe,
  Check,
  Loader2,
  ArrowRight,
  Code2,
  Cpu,
  Compass,
  ArrowUpRight,
  Users,
  Lightbulb,
} from "lucide-react";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

const PREFS = [
  { id: "video", icon: Video, title: "Video Meeting", tag: "Recommended", body: "Meet virtually at your scheduled appointment time." },
  { id: "phone", icon: Phone, title: "Phone Call", tag: null, body: "We’ll call you at your scheduled appointment time." },
  { id: "email", icon: Mail, title: "Intentional Email Conversation", tag: null, body: "Begin an email conversation at your scheduled appointment time instead of joining a live call." },
];

const PERSONAL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com",
  "aol.com", "icloud.com", "me.com", "mac.com", "proton.me",
  "protonmail.com", "zoho.com", "yandex.com", "gmx.com", "mail.com",
  "msn.com", "pm.me", "hey.com", "fastmail.com", "tutanota.com",
]);

function toISODate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatLongDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }).toUpperCase();
}

const StepChip = ({ n, label, active, done }) => (
  <div className="flex items-center gap-3">
    <div
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-medium",
        active && "border-[hsl(var(--brp-blue))]/70 text-[hsl(var(--brp-blue))] bg-[hsl(var(--brp-blue))]/10",
        done && "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
        !active && !done && "border-border/70 text-[hsl(var(--brp-ink-dim))]"
      )}
    >
      {done ? <Check className="h-3.5 w-3.5" /> : n}
    </div>
    <div className={cn("text-[11px] tracking-[0.22em] uppercase", active ? "text-[hsl(var(--brp-ink))]" : "text-[hsl(var(--brp-ink-dim))]") }>{label}</div>
  </div>
);

export default function ContactPage() {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [taken, setTaken] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    organization: "",
    email: "",
    notes: "",
    preference: "video",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [errors, setErrors] = useState({});

  const iso = useMemo(() => toISODate(date), [date]);

  useEffect(() => {
    let ignore = false;
    async function fetchSlots() {
      if (!iso) return;
      setLoadingSlots(true);
      try {
        const res = await getTakenSlots(iso);
        if (!ignore) setTaken(res.taken || []);
      } catch (e) {
        console.error(e);
        if (!ignore) setTaken([]);
      } finally {
        if (!ignore) setLoadingSlots(false);
      }
    }
    fetchSlots();
    return () => { ignore = true; };
  }, [iso]);

  const emailDomain = (form.email || "").split("@")[1]?.toLowerCase() || "";
  const isPersonalDomain = emailDomain && PERSONAL_DOMAINS.has(emailDomain);

  function validate() {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!form.organization.trim()) e.organization = "Please enter your organization.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    else if (isPersonalDomain) e.email = "Please use your company email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    if (!date || !time) {
      toast.error("Please select a date and time first.");
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const booking = await createBooking({
        full_name: form.full_name.trim(),
        organization: form.organization.trim(),
        email: form.email.trim(),
        date: iso,
        time,
        preference: form.preference,
        notes: form.notes.trim() || null,
      });
      setConfirmed(booking);
      toast.success("Conversation scheduled", { description: `${formatLongDate(date)} at ${time}` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      if (err?.response?.status === 409) {
        setTaken((t) => [...new Set([...t, time])]);
        setTime(null);
        setStep(2);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // StepChip moved out below to avoid nested component definition.

  return (
    <SiteLayout>
      <Toaster theme="dark" position="top-center" richColors closeButton />

      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 brp-hero-glow" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 pt-14 pb-24 sm:pt-20 sm:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* LEFT COLUMN */}
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">
                  <span className="h-px w-8 bg-[hsl(var(--brp-blue))]" /> Let’s Talk
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-6 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl leading-[1.02]">
                  Schedule a Conversation
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-md text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                  Whether you’re exploring custom software, intelligent systems, strategic technology consulting, workflow automation, digital transformation, or looking for a trusted technology partner, we’d love to learn more about your organization and how BRP Tech can help.
                </p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-12">
                  <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">Preferred Engagements</div>
                  <ul className="mt-4 divide-y divide-border/70 border-y border-border/70">
                    {[
                      { icon: Code2, label: "Custom Software" },
                      { icon: Cpu, label: "Intelligent Systems" },
                      { icon: Compass, label: "Strategic Technology Consulting" },
                      { icon: ArrowUpRight, label: "Digital Transformation" },
                    ].map((r) => (
                      <li key={r.label} className="flex items-center gap-4 py-4">
                        <div className="h-9 w-9 rounded-lg border border-border/70 grid place-items-center text-[hsl(var(--brp-blue))]">
                          <r.icon className="h-4 w-4" />
                        </div>
                        <div className="text-[hsl(var(--brp-ink))]">{r.label}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-12">
                  <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">What to Expect</div>
                  <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed max-w-md">
                    Our introductory conversation is an opportunity to learn about your organization, understand your goals, discuss your current challenges, and explore whether BRP Tech is the right technology partner for your needs.
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-md">
                    {[
                      { icon: Users, label: "No preparation is required." },
                      { icon: Lightbulb, label: "Simply bring your ideas." },
                      { icon: ArrowRight, label: "We’ll take it from there." },
                    ].map((r) => (
                      <div key={r.label} className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full border border-border/70 grid place-items-center text-[hsl(var(--brp-blue))] shrink-0">
                          <r.icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-[13px] text-[hsl(var(--brp-ink-dim))] leading-snug">{r.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* RIGHT COLUMN - SCHEDULER */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-5 sm:p-6 shadow-brp">
                  {/* Stepper */}
                  <div className="grid grid-cols-3 gap-2 pb-5 border-b border-border/70">
                    <StepChip n="1" label="Select Date" active={step === 1 && !confirmed} done={!!date} />
                    <StepChip n="2" label="Select Time" active={step === 2 && !confirmed} done={!!time} />
                    <StepChip n="3" label="Your Details" active={step === 3 && !confirmed} done={!!confirmed} />
                  </div>

                  {confirmed ? (
                    <div className="py-10 text-center">
                      <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center">
                        <Check className="h-6 w-6" />
                      </div>
                      <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">Conversation Scheduled</h3>
                      <p className="mt-2 text-[hsl(var(--brp-ink-dim))]">
                        {formatLongDate(date)} at {time} — {confirmed.preference === "video" ? "Video Meeting" : confirmed.preference === "phone" ? "Phone Call" : "Intentional Email Conversation"}
                      </p>
                      <p className="mt-4 text-sm text-[hsl(var(--brp-ink-dim))] max-w-md mx-auto">
                        Thank you for booking. A member of the BRP Tech team will be in touch at {confirmed.email} with the meeting details.
                      </p>
                      <div className="mt-8 flex items-center justify-center gap-3">
                        <Button
                          variant="outline"
                          className="rounded-full border-border/70"
                          onClick={() => {
                            setConfirmed(null);
                            setDate(null); setTime(null); setStep(1);
                            setForm({ full_name: "", organization: "", email: "", notes: "", preference: "video" });
                          }}
                        >
                          Schedule Another
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-6">
                      {/* Calendar column */}
                      <div className="md:col-span-6">
                        <div
                          data-testid={CONTACT.calendar}
                          className="rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-3))] p-2"
                        >
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => {
                              setDate(d);
                              setTime(null);
                              if (d) setStep(2);
                            }}
                            disabled={(d) => {
                              const today = new Date();
                              today.setHours(0,0,0,0);
                              if (d < today) return true;
                              const w = d.getDay();
                              return w === 0 || w === 6; // no weekends
                            }}
                            className="bg-transparent p-0"
                          />
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--brp-ink-dim))]">
                          <Globe className="h-3.5 w-3.5" /> All times shown in <span className="text-[hsl(var(--brp-ink))]">Eastern Time (ET)</span>
                        </div>
                      </div>

                      {/* Time slots column */}
                      <div className="md:col-span-6">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-[hsl(var(--brp-ink-dim))]">
                          {date ? formatLongDate(date) : "Select a date to see times"}
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-2 min-h-[220px]">
                          {!date && (
                            <div className="col-span-2 h-48 grid place-items-center text-sm text-[hsl(var(--brp-ink-dim))] border border-dashed border-border/70 rounded-lg">
                              Pick a date on the calendar
                            </div>
                          )}
                          {date && TIME_SLOTS.map((s) => {
                            const isTaken = taken.includes(s);
                            const isSelected = time === s;
                            return (
                              <button
                                key={s}
                                type="button"
                                data-testid={CONTACT.timeSlot}
                                data-slot={s}
                                disabled={isTaken || loadingSlots}
                                onClick={() => {
                                  setTime(s);
                                  setStep(3);
                                }}
                                className={cn(
                                  "h-11 rounded-lg border text-sm brp-focus transition-colors",
                                  isSelected
                                    ? "bg-[hsl(var(--brp-blue))] text-white border-[hsl(var(--brp-blue))]"
                                    : "bg-transparent border-border/70 text-[hsl(var(--brp-ink))] hover:bg-white/5",
                                  isTaken && "opacity-40 cursor-not-allowed line-through"
                                )}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Details step (opens beneath when time picked) */}
                {!confirmed && time && (
                  <div className="mt-6 rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-5 sm:p-6">
                    <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">A Few Details Before We Meet</div>
                    <p className="mt-1 text-sm text-[hsl(var(--brp-ink-dim))]">To help us make the most of our conversation, we’d love to learn a little about you.</p>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="full_name" className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Full Name</Label>
                        <Input
                          id="full_name"
                          data-testid={CONTACT.nameInput}
                          className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70 h-11"
                          placeholder="Enter your full name"
                          value={form.full_name}
                          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                        />
                        {errors.full_name && <p className="mt-2 text-xs text-red-400">{errors.full_name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="organization" className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Organization</Label>
                        <Input
                          id="organization"
                          data-testid={CONTACT.orgInput}
                          className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70 h-11"
                          placeholder="Enter your organization"
                          value={form.organization}
                          onChange={(e) => setForm({ ...form, organization: e.target.value })}
                        />
                        {errors.organization && <p className="mt-2 text-xs text-red-400">{errors.organization}</p>}
                      </div>
                    </div>

                    <div className="mt-4">
                      <Label htmlFor="email" className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">Company Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid={CONTACT.emailInput}
                        className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70 h-11"
                        placeholder="name@company.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      <p className="mt-2 text-xs text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                        BRP Tech currently schedules introductory conversations with businesses and institutional representatives using verified company domains.
                      </p>
                      {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
                    </div>

                    <div className="mt-6">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="notes" className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--brp-ink-dim))]">What Are You Solving For?</Label>
                        <span className="text-[11px] italic text-[hsl(var(--brp-ink-dim))]">(optional)</span>
                      </div>
                      <Textarea
                        id="notes"
                        data-testid={CONTACT.notesTextarea}
                        rows={4}
                        className="mt-2 bg-[hsl(var(--brp-surface-3))] border-border/70"
                        placeholder="Tell us a little about the current challenge you’re experiencing, the opportunity you’re exploring, or the outcome you’re hoping to achieve."
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      />
                    </div>

                    <div className="mt-8">
                      <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">Conversation Preference</div>
                      <p className="mt-1 text-sm text-[hsl(var(--brp-ink-dim))]">How would you like to begin the conversation?</p>
                      <RadioGroup
                        value={form.preference}
                        onValueChange={(v) => setForm({ ...form, preference: v })}
                        className="mt-4 grid grid-cols-1 gap-3"
                      >
                        {PREFS.map((p) => (
                          <label
                            key={p.id}
                            htmlFor={`pref-${p.id}`}
                            data-testid={CONTACT.radioCard}
                            className={cn(
                              "cursor-pointer rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-3))] p-4 flex items-start gap-4 hover:border-[hsl(var(--brp-blue))]/60 transition-colors",
                              form.preference === p.id && "border-[hsl(var(--brp-blue))]/70 ring-1 ring-[hsl(var(--brp-blue))]/30"
                            )}
                          >
                            <RadioGroupItem id={`pref-${p.id}`} value={p.id} className="mt-1" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[hsl(var(--brp-ink))] font-medium">{p.title}</span>
                                {p.tag && <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--brp-blue))] border border-[hsl(var(--brp-blue))]/40 rounded-full px-2 py-0.5">{p.tag}</span>}
                              </div>
                              <p className="mt-1 text-sm text-[hsl(var(--brp-ink-dim))] leading-relaxed">{p.body}</p>
                            </div>
                            <p.icon className="h-5 w-5 text-[hsl(var(--brp-blue))] shrink-0 mt-1" />
                          </label>
                        ))}
                      </RadioGroup>
                    </div>

                    <div className="mt-8">
                      <Button
                        data-testid={CONTACT.submit}
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full h-12 rounded-xl bg-gradient-to-r from-[hsl(var(--brp-blue))] via-[#4a7ce9] to-[hsl(var(--brp-orange))] text-white font-semibold tracking-wide hover:opacity-95"
                      >
                        {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Scheduling…</>) : "Schedule Conversation"}
                      </Button>
                      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[hsl(var(--brp-ink-dim))]">
                        <Lock className="h-3.5 w-3.5" /> Your information is secure and will never be shared.
                      </div>
                      <div className="mt-4 text-center text-xs text-[hsl(var(--brp-ink-dim))]">
                        Don’t have a company email yet? <a href="mailto:hello@brptech.com" className="text-[hsl(var(--brp-blue))] hover:underline">Contact us separately</a> to discuss your situation.
                      </div>
                    </div>
                  </div>
                )}
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-24 flex justify-center">
              <p className="text-center font-display text-2xl sm:text-3xl lg:text-4xl tracking-[-0.02em] max-w-3xl">
                Every meaningful innovation begins with a{" "}
                <span className="text-[hsl(var(--brp-blue))]">conversation</span>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
